import { Button, Tooltip } from '@dixa/lib-shared-habla';
import type { Conversation } from 'lib-shared-legacy-types';
import debounce from 'lodash/debounce';
import * as React from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { CopilotAction } from 'components/copilotDropdown';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { ImproveReply } from 'components/copilotPinnedActions/components/improveReply';
import { SpellingAndGrammar } from 'components/copilotPinnedActions/components/spellingAndGrammar';
import {
  DEFAULT_PINNED_STATE,
  EditorCopilotDropdown,
} from 'components/editorCopilotDropdown';
import {
  EditorCopilotDropdownFeedback,
  EditorCopilotDropdownImproveReply,
  EditorCopilotDropdownSpellingAndGrammar,
  EditorCopilotDropdownTranslate,
} from 'components/editorCopilotDropdown/components';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import { ValidationState } from 'components/shared/util/richTextField/components/components/stateBar/stateBar.types';
import { copilotUtils } from 'utils/copilot';

import { ExpandText } from '../../../../components/copilotPinnedActions/components/expandText';
import { EditorCopilotDropdownExpandText } from '../../../../components/editorCopilotDropdown/components/expandText/copilotDropdownExpandText';
import { getRequestID } from '../../shared/util/request';
import RichTextField from '../../shared/util/richTextField/components/richTextField';
import styles from '../styles/noteForm.module.scss';

type PropsT = {
  className?: string;
  csid: Conversation['id'];
  agentId: string;
  conversationAddWrapup: (payload: any) => void;
  tempWrapup: string;
  conversationSaveTempWrapup: (csid: any, tempWrapup: string) => void;
};

type StateT = {
  validation: ValidationState;
  isUploadingAttachments: boolean;
  editorCopilotActions: Record<string, CopilotAction>;
  editorText: string;
};

const DEFAULT_MAX_MESSAGE_LENGTH = 50000;

const getEditorCopilotOptions = (
  csid?: string,
): Record<string, CopilotAction> => {
  return {
    improveReply: {
      title: 'Improve note',
      icon: 'pen',
      commandName: 'richTextEditor:improveReply',
      content: <EditorCopilotDropdownImproveReply lastMessage="" csid={csid} />,
      isPinned: ({ pinnedState }) =>
        pinnedState?.improveReply ?? DEFAULT_PINNED_STATE.improveReply,
    },
    translate: {
      title: 'Translate',
      icon: 'translate',
      commandName: 'richTextEditor:translate',
      content: <EditorCopilotDropdownTranslate />,
    },
    checkSpellingAndGrammar: {
      title: 'Fix spelling and grammar',
      commandName: 'richTextEditor:checkSpellingAndGrammar',
      icon: 'grammar-spelling',
      content: <EditorCopilotDropdownSpellingAndGrammar />,
      isPinned: ({ pinnedState }) =>
        pinnedState?.checkSpellingAndGrammar ??
        DEFAULT_PINNED_STATE.checkSpellingAndGrammar,
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.spellingAndGrammar.getStatus({
            text: currentEditorText || '',
          });

        if (isDisabled) {
          return tooltipContent;
        }

        return false;
      },
    },
    expandText: {
      title: 'Expand note',
      icon: 'chat-plus',
      commandName: 'richTextEditor:expandText',
      content: <EditorCopilotDropdownExpandText lastMessage="" csid={csid} />,
      isPinned: ({ pinnedState }) =>
        pinnedState?.expandText ?? DEFAULT_PINNED_STATE.expandText,
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.expandText.getStatus({
            text: currentEditorText || '',
          });

        if (isDisabled) {
          return tooltipContent;
        }

        return false;
      },
    },
    sendFeedback: {
      title: 'Send feedback',
      icon: 'paper-plane',
      hasDividerBefore: true,
      content: <EditorCopilotDropdownFeedback />,
    },
  } as const satisfies Record<string, CopilotAction>;
};

export default class NoteForm extends React.Component<PropsT, StateT> {
  editorRef: React.RefObject<any>;

  constructor(props: PropsT) {
    super(props);
    this.state = {
      validation: { touched: false, valid: false },
      isUploadingAttachments: false,
      editorCopilotActions: getEditorCopilotOptions(props.csid?.toString()),
      editorText: '',
    };
    this.editorRef = React.createRef();

    this.handleTypingEvent = debounce(this.handleTypingEvent.bind(this), 500);
  }

  componentDidMount() {
    this.loadTempValue();
    this.editorRef.current.focus();
  }

  componentDidUpdate(prevProps: PropsT) {
    const { csid } = this.props;
    if (csid !== prevProps.csid) {
      this.saveTempValue(prevProps.csid);
      this.loadTempValue();
    }
  }

  componentWillUnmount() {
    const { csid } = this.props;
    this.saveTempValue(csid);
  }

  loadTempValue() {
    const { tempWrapup } = this.props;
    if (this.editorRef && this.editorRef.current) {
      this.editorRef.current.setText(tempWrapup);
    }
  }

  saveTempValue(csid: PropsT['csid']) {
    const { conversationSaveTempWrapup } = this.props;
    if (this.editorRef && this.editorRef.current) {
      const textContent = this.editorRef.current.getText();
      if (typeof textContent === 'string') {
        conversationSaveTempWrapup(csid, textContent.trim());
      }
    }
  }

  setValidationState = (validation: ValidationState) => {
    this.setState({ validation });
  };

  handleFileIsUploadingChange = (isUploadingAttachments: boolean) => {
    this.setState({ isUploadingAttachments });
  };

  handleSubmit = (): void => {
    const text = this.editorRef.current.getText();

    if (text && typeof text === 'string' && text.trim() !== '') {
      if (text.length > DEFAULT_MAX_MESSAGE_LENGTH) {
        this.setValidationState({
          touched: true,
          valid: false,
          error: { type: 'length' },
        });
        return;
      }

      const {
        csid,
        agentId,
        conversationSaveTempWrapup,
        conversationAddWrapup,
      } = this.props;

      const requestID = getRequestID();

      conversationAddWrapup({
        requestID: requestID,
        csid: csid,
        note: text,
        agentId: agentId,
      });

      this.editorRef.current.setText('');
      this.setValidationState({ touched: false, valid: true });
      conversationSaveTempWrapup(csid, '');
      return;
    }

    this.setValidationState({
      touched: true,
      valid: false,
      error: { type: 'required' },
    });
  };

  handleTypingEvent = (editorText: string) => {
    this.setState({ editorText });
  };
  render() {
    const tooltipContent = (
      <CommandKeybindingsTooltipContent commandName="richTextEditor:send">
        Add internal note
      </CommandKeybindingsTooltipContent>
    );

    return (
      <div className={styles.form}>
        <div className="conversation-form__editorwrap">
          <RichTextField
            className={`conversation-form__editornew ${styles.editor}`}
            ref={this.editorRef}
            channel="notes"
            placeholder="Type your internal note"
            onChange={this.handleTypingEvent}
            onFileIsUploadingChange={this.handleFileIsUploadingChange}
            onTriggerSend={() => this.handleSubmit()}
            validation={this.state.validation}
            setValidationState={this.setValidationState}
            toolbarAddons={
              <>
                <EditorMentions />
                <EditorQuickRepliesDropdown />
                <EditorCopilotDropdown
                  editorCopilotActions={this.state.editorCopilotActions}
                />
                <CopilotPinnedActions
                  text={this.state.editorText ?? ''}
                  csid={this.props.csid?.toString()}
                  pinnableActions={[
                    {
                      id: 'improveReply',
                      component: ImproveReply,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['improveReply'],
                      label: 'Improve note',
                    },
                    {
                      id: 'checkSpellingAndGrammar',
                      component: SpellingAndGrammar,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['checkSpellingAndGrammar'],
                    },
                    {
                      id: 'expandText',
                      component: ExpandText,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['expandText'],
                      label: 'Expand note',
                    },
                  ]}
                />
              </>
            }
          />
        </div>

        <div className="conversation-form__submit">
          <Tooltip content={tooltipContent} wrap="div">
            <Button
              type="button"
              disabled={this.state.isUploadingAttachments}
              onClick={this.handleSubmit}
            >
              {this.state.isUploadingAttachments ? 'Uploading' : 'Add note'}
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  }
}
